import { Box } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import { useMemo, type ReactNode } from 'react';

import { ChatWindow, type ChatWindowProps } from '@verantos-dev/shared-components';

import { GatewayChatSessionsPanel } from './GatewayChatSessionsPanel';
import { GatewayProtocolMonitor } from './GatewayProtocolMonitor';

type SessionsPanelProps = React.ComponentProps<typeof GatewayChatSessionsPanel>;
type ProtocolMonitorProps = React.ComponentProps<typeof GatewayProtocolMonitor>;

export interface GatewayChatLayoutProps {
  sessionsPanelProps: SessionsPanelProps;
  chatWindowProps: ChatWindowProps;
  protocolMonitorProps?: ProtocolMonitorProps;
  showProtocolMonitor?: boolean;
  rightSidebar?: ReactNode;
  sx?: SxProps<Theme>;
  chatWindowContainerSx?: SxProps<Theme>;
}

export const GatewayChatLayout = ({
  sessionsPanelProps,
  chatWindowProps,
  protocolMonitorProps,
  showProtocolMonitor = true,
  rightSidebar,
  sx,
  chatWindowContainerSx,
}: GatewayChatLayoutProps) => {
  const { shouldRenderMonitor, monitorContent } = useMemo(() => {
    const hasMonitorContent = Boolean(rightSidebar) || Boolean(protocolMonitorProps);
    const canRender = showProtocolMonitor && hasMonitorContent;

    if (!canRender) {
      return { shouldRenderMonitor: false, monitorContent: null as ReactNode };
    }

    const content =
      rightSidebar ??
      (protocolMonitorProps ? <GatewayProtocolMonitor {...protocolMonitorProps} /> : null);

    return {
      shouldRenderMonitor: Boolean(content),
      monitorContent: content,
    };
  }, [protocolMonitorProps, rightSidebar, showProtocolMonitor]);

  const gridTemplateColumns = shouldRenderMonitor
    ? '280px minmax(640px, 1fr) 320px'
    : '280px minmax(640px, 1fr)';

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns,
        height: 'calc(100vh - 51px)',
        backgroundColor: 'background.default',
        overflow: 'hidden',
        ...(sx ?? {}),
      }}
    >
      <GatewayChatSessionsPanel {...sessionsPanelProps} />

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          backgroundColor: 'background.paper',
          ...(chatWindowContainerSx ?? {}),
        }}
      >
        <ChatWindow {...chatWindowProps} />
      </Box>

      {shouldRenderMonitor ? monitorContent : null}
    </Box>
  );
};
